<template>
	<div
		class="ao-checkbox"
		:class="{'v-input--error': error}"
	>
		<label
			:class="[
				$style.container,
				isNarrow && $style['is-narrow'],
				color && $style[`container--bg-${color}`],
				density && $style[`container--density-${density}`],
				disabled && $style['container--disabled'],
				persistentBackground && $style['container--persistent-bg'],
				!background && $style['container--without-bg'],
				fit && $style.fit,
				identRight && $style['indent-right'],
				isLabelMultiRow ? $style['multi-row'] : $style['single-row'],
			]"
		>
			<span :class="[
				$style.title,
				'ao-p2-regular ao-checkbox-title',
				{ 'text-error': error },
			]">
				<slot>{{ label }}</slot>
			</span>
			<input
				type="checkbox"
				:checked="modelValue"
				:disabled="disabled"
				@change="$emit('update:modelValue', !modelValue)"
			>
			<span :class="[$style.checkmark]">
				<AoIcon
					v-if="partially"
					:class="$style['checkmark--icon']"
					iconName="checkMarkPartially"
				/>
				<AoIcon
					v-else
					:class="$style['checkmark--icon']"
					iconName="checkMark"
				/>
			</span>
		</label>
		<div
			v-if="error && errorMessages.length"
			class="v-input__details pa-0"
		>
			<div class="v-messages">
				<div
					v-for="(errorMessage, idx) in errorMessages"
					:key="idx"
					class="v-messages__message"
				>
					<span>
						{{ errorMessage }}
					</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import AoIcon from './AoIcon.vue'

defineProps({
	color: {
		type: String,
		default: 'primary',
		validator: (v) => ['primary', 'secondary'].includes(v),
	},
	density: {
		type: String,
		default: 'default',
		validator: (v) => ['compact', 'comfortable', 'default'].includes(v),
	},
	disabled: Boolean,
	label: String,
	modelValue: Boolean,
	persistentBackground: Boolean,
	background: {
		type: Boolean,
		default: true,
	},
	partially: {
		type: Boolean,
		default: false,
	},
	isNarrow: {
		type: Boolean,
		default: true,
	},
	fit: Boolean,
	identRight: Boolean,
	isLabelMultiRow: {
		type: Boolean,
	},
	error: {
		type: Boolean,
		default: false,
	},
	errorMessages: {
		type: [String, Array],
		default: () => ([]),
	},
})

defineEmits({
	'update:modelValue': null,
})
</script>

<style module lang="scss">
$checkbox-height: 36px;
.is-narrow {
	&.container {
		padding-left: 22px;

		&--bg {
			&-primary {
				&:hover {
					background: transparent;
				}
			}

			&-secondary {
				&:hover {
					background: transparent;
				}
			}
		}

		.checkmark {
			left: 0;
		}
	}
}

.fit {
	&.container {
		padding: 0 !important;

		.checkmark {
			top: 0;
			transform: translateX(0);
		}
	}
}

.indent-right {
	&.container {
		padding-right: 0 !important;
	}
}

.single-row {
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;
}

.multi-row {
	height: auto !important;
}

.container {
	position: relative;

	display: flex;
	align-items: center;
	padding-left: 34px;
	padding-right: 16px;

	border-radius: 4px;

	cursor: pointer;

	-webkit-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	user-select: none;

  // .title {
	// }

	input ~ .checkmark .checkmark--icon {
		display: none;
	}

	input:checked ~ .checkmark .checkmark--icon {
		display: block;
	}

	&--bg {
		&-primary {
			&:hover {
				background: #f0f4f7;
			}

			&:has(input:checked) {
				color: rgb(var(--v-theme-primary)) !important;
			}

			input:checked ~ .checkmark {
				background: rgb(var(--v-theme-primary)) !important;
				border: none;
			}

			input:checked ~ .checkmark .checkmark--icon {
				color: rgb(var(--v-theme-on-primary));
			}
		}

		&-secondary {
			color: rgb(var(--v-theme-on-secondary)) !important;

			&:hover {
				background: #f0f4f7;
			}

			&:has(input:checked) {
				color: rgb(var(--v-theme-on-secondary)) !important;
			}

			input:checked ~ .checkmark {
				background: rgb(var(--v-theme-primary)) !important;
				border: none;
			}

			input:checked ~ .checkmark .checkmark--icon {
				color: rgb(var(--v-theme-on-primary));
			}
		}

		// other colors
	}

	&--without-bg {
		padding-left: 20px;
		padding-right: 0px;
		border-radius: 0px;

		&:hover {
			background: #0000;
		}

		.checkmark {
			left: 0;
		}
	}

	&--persistent-bg.container--bg-primary,
	&--persistent-bg.container--bg-secondary {
		&:has(input:checked) {
			background: #f0f4f7;
		}
	}

	&--density {
		&-default {
			height: calc($checkbox-height);
		}

		&-compact {
			height: calc($checkbox-height - 12px);
		}

		&-comfortable {
			height: calc($checkbox-height - 8px);
		}

		&-tab-block {
			height: calc($checkbox-height - 16px);
		}
	}

	&--disabled {
		cursor: unset;
		pointer-events: none;

		& > * {
			opacity: 0.4;
		}
	}
}

// hide browser checkbox
.container input {
	position: absolute;
	opacity: 0;
	cursor: pointer;
	height: 0;
	width: 0;
}

// custom checkbox
.checkmark {
	position: absolute;
	top: 50%;
	left: 12px;
	transform: translateY(-50%);

	display: flex;
	justify-content: center;
	align-items: center;

	width: 13.33px;
	height: 13.33px;

	border: 1px solid #86919d;
	border-radius: 2.66667px;

	font-size: 10px;

	background: transparent;
}
</style>
